import React from 'react';
import { Link } from 'react-router-dom';
import {
  Phone, Mail, MapPin, User, Wrench, Camera, CreditCard, Plus, Printer, ExternalLink,
} from 'lucide-react';
import PosV2SavTimeline from '../pos-v2/PosV2SavTimeline';
import { RepField, RepSection } from './reparationsUi';
import { fmtEuro, repairFinancials } from './reparationsUtils';
import RepairResteDisplay from './RepairResteDisplay';
import { PARTS_LABEL_BY_KEY } from './reparationsParts';

const RepairFicheView = ({
  device,
  acomptes = [],
  onStatusClick,
  onAddAcompte,
  onPrintAcompte,
  photosSlot,
  notesSlot,
  repairStockSlot,
  avoirs = [],
  onPrintAvoir,
  onDownloadAvoirPdf,
}) => {
  const { total, acompte, reste } = repairFinancials(device);
  const received = device.created_at
    ? new Date(device.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';
  const partsEntries = device.parts_check
    ? Object.entries(device.parts_check).filter(([, v]) => v)
    : [];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4" data-testid="repair-detail-page">
      <div className="space-y-4 min-w-0">
        <RepSection title="Client" testId="client-info-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <RepField label="Nom" testId="client-name">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <User size={14} className="text-gray-500 shrink-0" />
                {device.client_name || '—'}
              </div>
            </RepField>
            <RepField label="Téléphone" testId="client-phone">
              {device.client_phone ? (
                <a href={`tel:${device.client_phone}`} className="text-sm font-semibold text-white hover:text-red-300 flex items-center gap-2">
                  <Phone size={14} className="text-gray-500 shrink-0" />
                  {device.client_phone}
                </a>
              ) : (
                <span className="text-sm text-gray-500">—</span>
              )}
            </RepField>
            {device.client_email && (
              <RepField label="Email" testId="client-email">
                <a href={`mailto:${device.client_email}`} className="text-sm text-gray-200 hover:text-white flex items-center gap-2 break-all">
                  <Mail size={14} className="text-gray-500 shrink-0" />
                  {device.client_email}
                </a>
              </RepField>
            )}
            {device.client_address && (
              <RepField label="Adresse" testId="client-address" className="sm:col-span-2">
                <div className="text-sm text-gray-200 flex items-start gap-2">
                  <MapPin size={14} className="text-gray-500 shrink-0 mt-0.5" />
                  {device.client_address}
                </div>
              </RepField>
            )}
          </div>
        </RepSection>

        <RepSection title="Appareil" testId="device-info-card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <RepField label="Modèle" value={device.model} testId="device-model" />
            {device.imei_serial && (
              <RepField label="IMEI / N° série" testId="device-imei">
                <div className="text-sm font-mono text-gray-200">{device.imei_serial}</div>
              </RepField>
            )}
            {device.technician_name && (
              <RepField label="Technicien" value={device.technician_name} testId="device-tech" />
            )}
            <RepField label="Reçu le" value={received} />
            {device.device_lock && (
              <RepField label="Code verrou" testId="device-lock">
                <div className="text-sm text-gray-200">{device.device_lock}</div>
              </RepField>
            )}
          </div>

          {device.problem && (
            <div className="mt-4 p-3 rounded-lg bg-red-950/30 border border-red-500/25">
              <div className="text-[10px] text-red-400 font-bold uppercase mb-1 flex items-center gap-1.5">
                <Wrench size={12} /> Problème déclaré
              </div>
              <p className="text-sm text-gray-200" data-testid="device-problem">{device.problem}</p>
            </div>
          )}

          {device.notes && (
            <div className="mt-3 p-3 rounded-lg bg-black/25 border border-white/10">
              <div className="text-[10px] text-gray-500 font-bold uppercase mb-1">Notes de réception</div>
              <p className="text-sm text-gray-300" data-testid="device-notes">{device.notes}</p>
            </div>
          )}

          {partsEntries.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] text-gray-500 font-bold uppercase mb-2">Pièces signalées</div>
              <div className="flex flex-wrap gap-1.5">
                {partsEntries.map(([key]) => (
                  <span key={key} className="px-2 py-1 rounded-lg text-xs font-medium border bg-red-950/30 text-red-300 border-red-500/25">
                    {PARTS_LABEL_BY_KEY[key] || key}
                  </span>
                ))}
              </div>
            </div>
          )}
        </RepSection>

        {repairStockSlot}

        <PosV2SavTimeline device={device} />

        {photosSlot}

        {notesSlot}
      </div>

      <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
        <RepSection title="Financier" testId="financial-card">
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
              <div className="text-[10px] text-gray-500 uppercase">Total</div>
              <div className="text-base font-bold text-white tabular-nums" data-testid="total-price">{fmtEuro(total)} €</div>
            </div>
            <div className="rounded-lg bg-black/25 border border-white/10 p-3 text-center">
              <div className="text-[10px] text-gray-500 uppercase">Acompte</div>
              <div className="text-base font-bold text-emerald-400 tabular-nums" data-testid="total-acomptes">{fmtEuro(acompte)} €</div>
            </div>
            <div className="rounded-lg bg-black/25 border border-red-500/20 p-3 text-center">
              <div className="text-[10px] text-gray-500 uppercase">Reste</div>
              <div className={`text-base font-bold tabular-nums ${reste > 0 ? 'text-red-400' : 'text-emerald-400'}`} data-testid="reste-a-payer">
                <RepairResteDisplay
                  reste={reste}
                  avoirs={avoirs}
                  onPrintAvoir={onPrintAvoir}
                  onDownloadAvoirPdf={onDownloadAvoirPdf}
                />
              </div>
            </div>
          </div>
          <div className="space-y-2 text-sm border-t border-white/10 pt-3">
            {(device.price_diagnostic || 0) > 0 && (
              <div className="flex justify-between text-gray-400">
                <span>Diagnostic</span>
                <span className="text-white tabular-nums">{fmtEuro(device.price_diagnostic)} €</span>
              </div>
            )}
            <div className="flex justify-between text-gray-400">
              <span>Réparation</span>
              <span className="text-white tabular-nums">{fmtEuro(device.price_repair)} €</span>
            </div>
            {device.pricing_mode === 'grounded' && device.quoted_total != null && (
              <div className="flex justify-between text-gray-500 text-xs">
                <span>Devis initial</span>
                <span className="tabular-nums">{fmtEuro(device.quoted_total)} €</span>
              </div>
            )}
            {(device.remise || 0) > 0 && (
              <div className="flex justify-between text-gray-400">
                <span>Remise</span>
                <span className="text-emerald-400 tabular-nums">-{fmtEuro(device.remise)} €</span>
              </div>
            )}
          </div>
        </RepSection>

        <RepSection title="Acomptes" testId="acomptes-card">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">{acomptes.length} versement{acomptes.length !== 1 ? 's' : ''}</span>
            <button
              type="button"
              onClick={onAddAcompte}
              className="text-xs font-semibold text-red-400 hover:text-red-300 flex items-center gap-1"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
          {acomptes.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">Aucun acompte</p>
          ) : (
            <div className="space-y-2">
              {acomptes.map((ac) => (
                <div key={ac.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-black/25 border border-white/10 text-sm">
                  <div className="min-w-0">
                    <div className="font-bold text-emerald-400 tabular-nums">{fmtEuro(ac.montant)} €</div>
                    <div className="text-[10px] text-gray-500 truncate">
                      {ac.mode_paiement}
                      {ac.date_versement && ` · ${new Date(ac.date_versement).toLocaleDateString('fr-FR')}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onPrintAcompte?.(ac.id, ac.ref_acompte || ac.id)}
                    className="shrink-0 p-2 rounded-lg bg-white/10 hover:bg-white/15 text-gray-400"
                    title="Imprimer ticket"
                    data-testid={`print-acompte-ticket-${ac.id}`}
                  >
                    <Printer size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </RepSection>

        <RepSection title="Actions" testId="quick-actions-card">
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={onStatusClick}
              className="min-h-[44px] w-full px-4 rounded-lg bg-red-600 hover:bg-red-500 font-semibold text-sm text-white"
            >
              Changer le statut
            </button>
            {reste > 0 && (
              <Link
                to="/pos-v2"
                className="min-h-[44px] w-full px-4 rounded-lg bg-white/10 hover:bg-white/15 font-semibold text-sm flex items-center justify-center gap-2 text-white"
              >
                <CreditCard size={16} />
                Encaisser le reste ({fmtEuro(reste)} €)
              </Link>
            )}
            <Link
              to="/pos-v2"
              className="min-h-[44px] w-full px-4 rounded-lg border border-white/15 hover:bg-white/5 font-semibold text-sm flex items-center justify-center gap-2 text-gray-300"
            >
              <ExternalLink size={16} />
              Ouvrir en POS V2
            </Link>
          </div>
        </RepSection>

        <div className="text-[10px] text-gray-600 space-y-1 px-1" data-testid="dates-card">
          {device.created_at && (
            <div>Créé · {new Date(device.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          )}
          {device.updated_at && (
            <div>Modifié · {new Date(device.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default RepairFicheView;
